import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StageBadge, OutcomeBadge, money } from "@/components/opportunities/shared";
import { StatusBadge, formatDate } from "@/components/prospects/shared";
import { formatDateTime } from "@/lib/dates";
import OpportunityDialog from "@/components/opportunities/OpportunityDialog";
import { CloseWonDialog, CloseLostDialog, ReopenDialog, RejectCloseDialog } from "@/components/opportunities/CloseDialogs";
import HandoffDialog from "@/components/opportunities/HandoffDialog";
import { CorrectOriginDialog, CommissionCategoryDialog } from "@/components/opportunities/ClassificationDialogs";
import {
  ArrowLeft, CircleNotch, PencilSimple, Trophy, XCircle, ArrowCounterClockwise,
  Archive, ClockCounterClockwise, Buildings,
} from "@phosphor-icons/react";

async function fetchOppInto(setOpp, setHistory, oppId) {
  try {
    const [opp, history] = await Promise.all([
      api.get(`/opportunities/${oppId}`),
      api.get(`/opportunities/${oppId}/stage-history`),
    ]);
    setOpp(opp.data);
    setHistory(history.data);
  } catch (e) {
    toast.error(formatApiError(e.response?.data?.detail));
    setOpp(undefined);
  }
}

async function fetchStagesInto(setStages) {
  try {
    const { data } = await api.get("/reference/pipeline-stages");
    setStages(data);
  } catch (e) {
    toast.error("Pipeline stages could not be loaded.");
  }
}

function SummaryRow({ label, value, testId, strong }) {
  return (
    <div className="grid grid-cols-3 gap-3 border-b border-[#F4F4F5] py-2.5 text-sm last:border-0">
      <span className="text-xs font-bold uppercase tracking-wide text-[#6D6D70]">{label}</span>
      <span className={`col-span-2 break-words ${strong ? "font-heading text-base font-black text-[#032852]" : "text-[#09090B]"}`} data-testid={testId}>
        {value ?? "—"}
      </span>
    </div>
  );
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [opp, setOpp] = useState(null);
  const [history, setHistory] = useState([]);
  const [stages, setStages] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [wonOpen, setWonOpen] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [reopenOpen, setReopenOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [handoffOpen, setHandoffOpen] = useState(false);
  const [originDialogOpen, setOriginDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);

  const helpers = useRef({ fetchOppInto, fetchStagesInto });
  const load = useCallback(() => helpers.current.fetchOppInto(setOpp, setHistory, id), [helpers, setOpp, setHistory, id]);
  useEffect(() => { load(); }, [load]);
  const loadStages = useCallback(() => helpers.current.fetchStagesInto(setStages), [helpers, setStages]);
  useEffect(() => { loadStages(); }, [loadStages]);

  if (opp === null) {
    return <div className="flex items-center justify-center py-24"><CircleNotch size={26} className="animate-spin text-[#032852]" /></div>;
  }
  if (opp === undefined) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-[#6D6D70]">This opportunity could not be loaded.</p>
        <Button variant="outline" className="mt-4 rounded-sm" onClick={() => navigate("/opportunities")}>Back to opportunities</Button>
      </div>
    );
  }

  const isOpen = opp.outcome === "open" && opp.status !== "archived";
  const openStages = stages.filter((s) => !s.name.startsWith("Closed"));

  const transition = async (stageId) => {
    if (stageId === opp.pipeline_stage_id) return;
    const stage = stages.find((s) => s.id === stageId);
    if (stage?.name === "Closed - Won") return setWonOpen(true);
    if (stage?.name === "Closed - Lost") return setLostOpen(true);
    try {
      await api.post(`/opportunities/${opp.id}/transition`, { stage_id: stageId, version: opp.version });
      toast.success("Stage updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const archive = async () => {
    try {
      const action = opp.status === "archived" ? "restore" : "archive";
      await api.post(`/opportunities/${opp.id}/${action}`);
      toast.success(action === "archive" ? "Opportunity archived" : "Opportunity restored");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const propertiesDisplay = opp.all_properties
    ? "All properties (entire portfolio)"
    : (opp.properties || []).map((p) => p.name).join(", ") || "—";
  const missingProperty = isOpen && !opp.all_properties && (opp.property_ids || []).length === 0;

  const CREDIT_LABELS = {
    shirley_originated: "100% — Self-Originated (Owner-Generated)",
    company_assigned: "50% — Company/Owner-Assigned",
    tag_work: "Tag work — tracked separately from quota",
  };
  // Rule P6-2026.06 — pay plan comes from commission_category, NOT origin.
  const CATEGORY_LABELS = {
    fully_developed: "Fully Developed — 10% first month + 1% months 2–12",
    assisted: "Assisted — 7% first month + 1% months 2–12",
    tag_work: "Tag Work — 10% one-time",
  };

  return (
    <div className="p-6 sm:p-8" data-testid="opportunity-detail">
      <button onClick={() => navigate("/opportunities")} className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-[#6D6D70] hover:text-[#032852]" data-testid="back-to-opportunities">
        <ArrowLeft size={14} /> Opportunities
      </button>

      <div className="mt-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="font-heading text-2xl font-black tracking-tight text-[#032852] sm:text-3xl" data-testid="opp-detail-name">{opp.name}</h1>
            <StageBadge name={opp.stage_name} testId="opp-detail-stage" />
            {opp.location_setup_incomplete && (
              <span className="rounded-sm bg-[#F59E0B]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-[#B45309]" data-testid="location-incomplete-badge"
                title="Won without an actual included service location — add the real property under the organization, then correct the included scope. The award, credit and values are untouched.">
                Location setup incomplete
              </span>
            )}
            {opp.pricing_incomplete && (
              <span className="rounded-sm bg-[#F59E0B]/15 px-2 py-0.5 text-[10px] font-bold uppercase text-[#B45309]" data-testid="pricing-pending-badge"
                title="Contract opportunities need a positive monthly recurring amount (Tag sales need a one-time sale value). Missing pricing is never treated as a $0 one-time sale — add the real amount.">
                Pricing pending
              </span>
            )}
            <OutcomeBadge outcome={opp.outcome} />
            {opp.status === "archived" && <StatusBadge status="archived" testId="opp-archived-badge" />}
          </div>
          <Link to={`/prospects/${opp.prospect_id}`} className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-[#032852] hover:text-[#569A1F]" data-testid="opp-prospect-link">
            <Buildings size={15} /> {opp.prospect_name}
            {opp.prospect_lifecycle_status === "client" && <span className="rounded-sm bg-[#569A1F] px-1.5 py-0.5 text-[10px] font-bold uppercase text-white">Client</span>}
          </Link>
          {opp.close_requested && isOpen && (
            <p className="mt-2 w-fit rounded-sm bg-[#F59E0B]/10 px-3 py-1.5 text-xs font-semibold text-[#B45309]" data-testid="close-requested-flag">
              Prepared for closing — awaiting administrator confirmation of Closed – Won.
              {opp.close_requested_by_name ? ` Requested by ${opp.close_requested_by_name}` : ""}
              {opp.close_requested_at ? ` on ${formatDate(opp.close_requested_at)}.` : "."}
            </p>
          )}
          {opp.close_review_decision === "rejected" && !opp.close_requested && isOpen && (
            <p className="mt-2 w-fit rounded-sm bg-[#EF4444]/10 px-3 py-1.5 text-xs font-semibold text-[#B91C1C]" data-testid="close-rejected-flag">
              Closed-Won request rejected by {opp.close_review_decided_by_name || "an administrator"}
              {opp.close_review_decided_at ? ` on ${formatDate(opp.close_review_decided_at)}` : ""}
              {opp.close_review_rejection_reason ? ` — ${opp.close_review_rejection_reason}` : ""}.
              The opportunity remains open; address the reason and request again.
            </p>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isOpen && (
            <>
              <Select value={opp.pipeline_stage_id} onValueChange={transition}>
                <SelectTrigger className="w-56 rounded-sm" data-testid="detail-stage-select"><SelectValue /></SelectTrigger>
                <SelectContent>{stages.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button variant="outline" className="rounded-sm" onClick={() => setEditOpen(true)} data-testid="edit-opportunity-btn">
                <PencilSimple size={15} /> Edit
              </Button>
              <Button className="rounded-sm bg-[#569A1F] font-semibold text-white hover:bg-[#457d18]" onClick={() => setWonOpen(true)} data-testid="close-won-btn">
                <Trophy size={15} /> {isAdmin ? "Close Won" : "Prepare Close Won"}
              </Button>
              <Button variant="outline" className="rounded-sm border-[#EF4444]/40 text-[#B91C1C] hover:bg-[#EF4444]/5" onClick={() => setLostOpen(true)} data-testid="close-lost-btn">
                <XCircle size={15} /> Close Lost
              </Button>
              {isAdmin && opp.close_requested && (
                <Button variant="outline" className="rounded-sm border-[#EF4444]/40 text-[#B91C1C] hover:bg-[#EF4444]/5"
                  onClick={() => setRejectOpen(true)} data-testid="reject-close-request-btn">
                  <XCircle size={15} /> Reject Close Request
                </Button>
              )}
            </>
          )}
          {isAdmin && opp.outcome === "won" && (
            <Button variant="outline" className="rounded-sm" data-testid="correct-origin-btn"
              onClick={() => setOriginDialogOpen(true)}>
              Correct Quota Attribution
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" className="rounded-sm" data-testid="set-commission-category-btn"
              onClick={() => setCategoryDialogOpen(true)}>
              Commission Category
            </Button>
          )}
          {isAdmin && opp.outcome !== "open" && opp.status !== "archived" && (
            <Button variant="outline" className="rounded-sm" onClick={() => setReopenOpen(true)} data-testid="reopen-btn">
              <ArrowCounterClockwise size={15} /> Reopen
            </Button>
          )}
          {(opp.status !== "archived" || isAdmin) && (
            <Button variant="outline" className="rounded-sm" onClick={archive} data-testid="archive-opportunity-btn">
              <Archive size={15} /> {opp.status === "archived" ? "Restore" : "Archive"}
            </Button>
          )}
        </div>
      </div>

      {missingProperty && (
        <div className="mt-4 rounded-sm border border-[#B45309]/40 bg-[#F59E0B]/10 p-4 text-sm" data-testid="missing-property-banner">
          <p className="text-xs font-bold uppercase tracking-wide text-[#B45309]">Property required</p>
          <p className="mt-1 text-[#09090B]">
            This opportunity has no linked property. Every opportunity must include at least one property —
            use <span className="font-semibold">Edit</span> to add one before advancing the stage or closing.
          </p>
        </div>
      )}

      {isAdmin && opp.outcome === "won" && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 rounded-sm border border-[#E4E4E7] bg-white p-4 text-sm" data-testid="handoff-panel">
          <div className="min-w-0">
            <p className="text-xs font-bold uppercase tracking-wide text-[#032852]">Admin Hub Transfer</p>
            <p className="mt-1 flex items-center gap-2 text-xs">
              <span className={`rounded-sm px-2 py-0.5 font-bold ${{
                complete: "bg-[#569A1F]/10 text-[#2f5a0e]",
                needs_review: "bg-[#F59E0B]/10 text-[#B45309]",
                failed: "bg-[#EF4444]/10 text-[#B91C1C]",
              }[opp.handoff_status] || "bg-[#F59E0B]/10 text-[#B45309]"}`} data-testid="handoff-status-badge">
                {{ complete: "Complete", needs_review: "Needs Review", failed: "Failed" }[opp.handoff_status] || "Pending"}
              </span>
              {opp.handoff_error && <span className="text-[#B91C1C]">{opp.handoff_error}</span>}
              {opp.handoff_status === "complete" && opp.handoff_result?.customer_id && (
                <span className="text-[#52525B]">Hub customer <span className="font-mono font-semibold">{opp.handoff_result.customer_id}</span></span>
              )}
            </p>
          </div>
          {opp.handoff_status !== "complete" && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" className="rounded-sm" data-testid="handoff-check-status-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.get(`/opportunities/${opp.id}/handoff/status`);
                    if (data.hub_award) toast.success("Admin Hub already recorded this award — reconciled.");
                    else toast.info(data.note || "Admin Hub has no award for this opportunity yet.");
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Could not reach the Admin Hub");
                  }
                  load();
                }}>
                Check Hub Status
              </Button>
              <Button className="rounded-sm bg-[#032852] font-semibold text-white" onClick={() => setHandoffOpen(true)} data-testid="handoff-open-btn">
                {opp.handoff_status === "failed" || opp.handoff_status === "needs_review" ? "Review & Retry Transfer" : "Send to Admin Hub"}
              </Button>
            </div>
          )}
        </div>
      )}

      {opp.outcome === "lost" && (
        <div className="mt-4 rounded-sm border border-[#EF4444]/30 bg-[#EF4444]/5 p-4 text-sm" data-testid="loss-panel">
          <p className="text-xs font-bold uppercase tracking-wide text-[#B91C1C]">Closed – Lost {formatDate(opp.lost_date)}</p>
          <p className="mt-1 text-[#09090B]"><span className="font-semibold">Reason:</span> {opp.loss_reason}</p>
          {opp.competitor && <p className="text-[#09090B]"><span className="font-semibold">Competitor:</span> {opp.competitor}</p>}
        </div>
      )}

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <div className="rounded-sm border border-[#E4E4E7] bg-white p-5" data-testid="financial-summary">
          <h2 className="text-xl font-bold text-[#032852]">Financial Summary</h2>
          <div className="mt-2">
            <SummaryRow label="First 12-Month Contract Value" value={money(opp.first_12_month_revenue)} testId="opp-12mo-value" strong />
            <SummaryRow label="Total Contract Value (TCV)" testId="opp-tcv-value"
              value={opp.total_contract_value != null
                ? `${money(opp.total_contract_value)} — full ${opp.contract_term_months}-month term, informational only`
                : "— (requires contracted term and pricing)"} />
            <SummaryRow label="Monthly recurring" value={money(opp.monthly_recurring_revenue)} testId="opp-detail-mrr" />
            <SummaryRow label="One-time / project" value={money(opp.one_time_revenue)} testId="opp-detail-one-time" />
            <SummaryRow label="Full contract term" value={opp.contract_term_months ? `${opp.contract_term_months} months` : "—"} testId="opp-detail-term" />
            <SummaryRow label="Close probability" value={`${opp.stage_probability_pct ?? opp.stage_probability}% — stage-based forecast estimate, not a guarantee`} testId="opp-detail-probability" />
            <SummaryRow label="Included service locations" testId="opp-detail-locations" value={
              (opp.included_properties || []).length > 0
                ? opp.included_properties.map((p) => `${p.name}${p.address ? ` (${p.address})` : ""}`).join("; ")
                : (opp.all_properties ? "All properties — organization has none on file yet" : "—")
            } />
          </div>
          <p className="mt-3 text-[11px] text-[#6D6D70]">Quota and commission always use the first-12-month value only, credited once in the quota period of the booking — months 13+ and TCV never count. After month 12 the account is a house account.</p>
        </div>

        <div className="rounded-sm border border-[#E4E4E7] bg-white p-5" data-testid="opp-details-card">
          <h2 className="text-xl font-bold text-[#032852]">Details</h2>
          <div className="mt-2">
            <SummaryRow label="Current Owner" value={opp.owner_name} testId="opp-detail-owner" />
            <SummaryRow label="Created By" value={opp.created_by_name} testId="opp-detail-created-by" />
            <SummaryRow label="Lead Origin (source)" value={opp.lead_source_name} testId="opp-detail-lead-source" />
            <SummaryRow label="Quota Attribution (Lead Origin)" value={{ shirley_originated: "Self-Originated (Owner-Generated)", company_assigned: "Company/Owner-Assigned", tag_work: "Tag Work" }[opp.origin_classification] || "Not classified"} testId="opp-detail-origin" />
            <SummaryRow label="Quota Credit" value={CREDIT_LABELS[opp.origin_classification] || "—"} testId="opp-detail-quota-credit" />
            <SummaryRow label="Commission Category" value={CATEGORY_LABELS[opp.commission_category] || "Needs classification — no commissions generate until an administrator classifies this sale"} testId="opp-detail-commission-plan" />
            <SummaryRow label="Primary contact" value={opp.primary_contact_name} testId="opp-detail-contact" />
            <SummaryRow label="Divisions" value={(opp.service_division_names || []).join(", ") || "—"} testId="opp-detail-divisions" />
            <SummaryRow label="Properties" value={propertiesDisplay} testId="opp-detail-properties" />
            <SummaryRow label="Target close" value={formatDate(opp.target_close_date)} testId="opp-detail-target-close" />
            <SummaryRow label="Bid due" value={formatDate(opp.bid_due_date)} testId="opp-detail-bid-due" />
            <SummaryRow label="Service start" value={formatDate(opp.service_start_date)} testId="opp-detail-service-start" />
            {opp.won_date && <SummaryRow label="Won" value={formatDate(opp.won_date)} testId="opp-detail-won-date" />}
            {opp.closing_notes && <SummaryRow label="Notes" value={opp.closing_notes} testId="opp-detail-notes" />}
          </div>
        </div>

        <div className="rounded-sm border border-[#E4E4E7] bg-white p-5" data-testid="stage-history">
          <h2 className="flex items-center gap-2 text-xl font-bold text-[#032852]">
            <ClockCounterClockwise size={18} /> Stage History
          </h2>
          <div className="mt-3 space-y-3">
            {history.map((entry) => (
              <div key={entry.id} className="border-l-2 border-[#032852]/20 pl-3" data-testid={`history-entry-${entry.id}`}>
                <p className="text-sm font-semibold text-[#09090B]">
                  {entry.previous_stage_name ? `${entry.previous_stage_name} → ` : ""}{entry.new_stage_name}
                </p>
                <p className="text-xs text-[#6D6D70]">{formatDateTime(entry.changed_at)} · {entry.changed_by_name} · v{entry.opportunity_version}</p>
                {entry.note && <p className="mt-0.5 text-xs text-[#52525B]">{entry.note}</p>}
              </div>
            ))}
          </div>
        </div>
      </div>

      {originDialogOpen && <CorrectOriginDialog opp={opp} onClose={() => setOriginDialogOpen(false)} onDone={load} />}
      {categoryDialogOpen && <CommissionCategoryDialog opp={opp} onClose={() => setCategoryDialogOpen(false)} onDone={load} />}
      {editOpen && (
        <OpportunityDialog key={`edit-${opp.id}-v${opp.version}`} open opportunity={opp}
          onClose={() => setEditOpen(false)} onDone={load} />
      )}
      {wonOpen && (
        <CloseWonDialog key={`won-${opp.id}-v${opp.version}`} open opportunity={opp} isAdmin={isAdmin}
          onClose={() => setWonOpen(false)} onDone={load} />
      )}
      {lostOpen && (
        <CloseLostDialog key={`lost-${opp.id}-v${opp.version}`} open opportunity={opp}
          onClose={() => setLostOpen(false)} onDone={load} />
      )}
      {reopenOpen && (
        <ReopenDialog key={`reopen-${opp.id}-v${opp.version}`} open opportunity={opp} stages={stages}
          onClose={() => setReopenOpen(false)} onDone={load} />
      )}
      {rejectOpen && (
        <RejectCloseDialog key={`reject-${opp.id}-v${opp.version}`} open opportunity={opp}
          onClose={() => setRejectOpen(false)} onDone={load} />
      )}
      {handoffOpen && (
        <HandoffDialog open opportunity={opp} onClose={() => setHandoffOpen(false)} onDone={load} />
      )}
    </div>
  );
}
